import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getBolDraft } from "@/lib/bol.functions";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";

export const Route = createFileRoute(
  "/_authenticated/app/shipments/$shipmentId/bol/print",
)({ component: BolPrintPage });

function BolPrintPage() {
  const { shipmentId } = Route.useParams();
  const navigate = useNavigate();
  const fetchFn = useServerFn(getBolDraft);

  const { data, isLoading, error } = useQuery({
    queryKey: ["bol-print", shipmentId],
    queryFn: () => fetchFn({ data: { shipmentId } }),
  });
  const bol: any = data?.bol;

  useEffect(() => {
    document.title = bol?.bol_number ? `BOL ${bol.bol_number}` : "Bill of Lading";
  }, [bol]);

  if (isLoading) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;
  if (error) return <div className="p-10 text-sm text-destructive">{(error as Error).message}</div>;
  if (!bol) return <div className="p-10 text-sm">No BOL found for this shipment.</div>;

  const shipper = bol.shipper ?? {};
  const consignee = bol.consignee ?? {};
  const carrier = bol.carrier_info ?? {};
  const refs = bol.ref_numbers ?? {};
  const hu: any[] = Array.isArray(bol.handling_units) ? bol.handling_units : [];
  const lines: any[] = Array.isArray(bol.commodity_lines) ? bol.commodity_lines : [];
  const totals = bol.totals ?? {};

  return (
    <div className="min-h-screen bg-muted/40">
      <div className="print:hidden border-b bg-card">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" asChild>
            <Link to="/app/shipments/$shipmentId/bol" params={{ shipmentId }}>
              <ArrowLeft className="h-4 w-4 mr-2" />Back to BOL editor
            </Link>
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              Status: <span className="font-medium uppercase">{bol.status}</span>
              {bol.revision ? ` · Rev ${bol.revision}` : ""}
            </span>
            <Button onClick={() => window.print()}>
              <Printer className="h-4 w-4 mr-2" />Print / Save PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 print:p-0">
        <div className="bg-white text-black p-10 shadow-sm print:shadow-none print:p-4 text-[11px] leading-tight">
          {/* Title block */}
          <div className="flex items-start justify-between border-b-2 border-black pb-2 mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-600">Straight Bill of Lading — Short Form</div>
              <div className="text-2xl font-bold mt-1">BILL OF LADING</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase text-neutral-600">BOL Number</div>
              <div className="text-xl font-mono font-bold">{bol.bol_number ?? "DRAFT"}</div>
              <div className="text-[10px] mt-1">Page 1 of 1</div>
            </div>
          </div>

          {/* Shipper / Consignee */}
          <div className="grid grid-cols-2 gap-0 border border-black">
            <PartyBlock title="Ship From" party={shipper} />
            <PartyBlock title="Ship To" party={consignee} bordered />
          </div>

          {/* Carrier + References */}
          <div className="grid grid-cols-2 gap-0 border-l border-r border-b border-black">
            <div className="border-r border-black p-2">
              <SectionTitle>Carrier Information</SectionTitle>
              <KV k="Carrier" v={carrier.name} />
              <KV k="SCAC" v={carrier.scac} />
              <KV k="PRO #" v={carrier.pro} />
              <KV k="Trailer #" v={carrier.trailer} />
              <KV k="Driver" v={carrier.driver} />
              <KV k="Seal #" v={carrier.seal} />
            </div>
            <div className="p-2">
              <SectionTitle>Reference Numbers</SectionTitle>
              <KV k="PO #(s)" v={(refs.pos ?? []).join(", ")} />
              <KV k="Customer Ref(s)" v={(refs.customer_refs ?? []).join(", ")} />
              <KV k="Order #(s)" v={(refs.order_numbers ?? []).join(", ")} />
              <KV k="Shipment ID" v={refs.shipment_id} mono />
            </div>
          </div>

          {/* Handling units */}
          {hu.length > 0 && (
            <div className="border-l border-r border-b border-black">
              <SectionTitle padded>Handling Units (Pallets / SSCC)</SectionTitle>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-neutral-100 text-[10px] uppercase">
                    <th className="border-t border-black p-1 text-left">SSCC</th>
                    <th className="border-t border-black p-1 text-left">Type</th>
                    <th className="border-t border-black p-1 text-right">Qty</th>
                    <th className="border-t border-black p-1 text-right">Weight (lb)</th>
                    <th className="border-t border-black p-1 text-right">L×W×H (in)</th>
                  </tr>
                </thead>
                <tbody>
                  {hu.map((h, i) => (
                    <tr key={i} className="text-[11px]">
                      <td className="border-t border-black p-1 font-mono">{h.sscc ?? "—"}</td>
                      <td className="border-t border-black p-1">{h.type ?? "—"}</td>
                      <td className="border-t border-black p-1 text-right">{h.qty ?? 1}</td>
                      <td className="border-t border-black p-1 text-right">{h.weight_lb ?? "—"}</td>
                      <td className="border-t border-black p-1 text-right">
                        {[h.length_in, h.width_in, h.height_in].filter((v) => v != null).join(" × ") || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Commodity table */}
          <div className="border-l border-r border-b border-black">
            <SectionTitle padded>Commodity Description</SectionTitle>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-neutral-100 text-[10px] uppercase">
                  <th className="border-t border-black p-1 text-right">Qty</th>
                  <th className="border-t border-black p-1 text-left">Pkg Type</th>
                  <th className="border-t border-black p-1 text-left">Description</th>
                  <th className="border-t border-black p-1 text-left">NMFC</th>
                  <th className="border-t border-black p-1 text-left">Class</th>
                  <th className="border-t border-black p-1 text-right">Weight (lb)</th>
                  <th className="border-t border-black p-1 text-center">HM</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td className="border-t border-black p-1 text-right">{l.qty}</td>
                    <td className="border-t border-black p-1">{l.pkg_type}</td>
                    <td className="border-t border-black p-1">{l.description}</td>
                    <td className="border-t border-black p-1 font-mono">{l.nmfc || "—"}</td>
                    <td className="border-t border-black p-1">{l.freight_class || "—"}</td>
                    <td className="border-t border-black p-1 text-right">{l.weight_lb ?? "—"}</td>
                    <td className="border-t border-black p-1 text-center">{l.hazmat ? "X" : ""}</td>
                  </tr>
                ))}
                <tr className="bg-neutral-50 font-semibold">
                  <td className="border-t border-black p-1 text-right" colSpan={5}>
                    Totals — Pallets: {totals.total_pallets ?? 0} · Cartons: {totals.total_cartons ?? 0}
                  </td>
                  <td className="border-t border-black p-1 text-right">{totals.total_weight_lb ?? "—"}</td>
                  <td className="border-t border-black p-1"></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Legal copy */}
          <div className="mt-3 text-[9px] leading-snug text-neutral-700">
            RECEIVED, subject to individually determined rates or contracts that have been agreed upon in writing between the carrier
            and shipper, if applicable, otherwise to the rates, classifications and rules that have been established by the carrier
            and are available to the shipper, on request. The property described above, in apparent good order, except as noted
            (contents and condition of contents of packages unknown), marked, consigned, and destined as shown above.
          </div>

          {/* Signatures */}
          <div className="grid grid-cols-3 gap-0 border border-black mt-3">
            <SignBlock title="Shipper Signature / Date" />
            <SignBlock title="Carrier Signature / Pickup Date" bordered />
            <SignBlock title="Consignee Signature / Date" bordered />
          </div>

          <div className="text-[9px] text-neutral-500 mt-3 flex justify-between">
            <span>{shipper.name}</span>
            <span>Generated {new Date().toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children, padded }: { children: React.ReactNode; padded?: boolean }) {
  return (
    <div className={`text-[10px] uppercase tracking-wider font-semibold text-neutral-700 ${padded ? "px-2 pt-2" : ""}`}>
      {children}
    </div>
  );
}

function KV({ k, v, mono }: { k: string; v?: any; mono?: boolean }) {
  return (
    <div className="flex text-[11px] py-[1px]">
      <span className="w-28 text-neutral-600">{k}</span>
      <span className={mono ? "font-mono" : ""}>{v ? String(v) : "—"}</span>
    </div>
  );
}

function PartyBlock({ title, party, bordered }: { title: string; party: any; bordered?: boolean }) {
  return (
    <div className={`p-2 ${bordered ? "border-l border-black" : ""}`}>
      <SectionTitle>{title}</SectionTitle>
      <div className="font-semibold mt-1">{party?.name || "—"}</div>
      <div className="whitespace-pre-line text-[11px]">{party?.address || "—"}</div>
      {party?.contact && <div className="text-[11px] mt-1">Contact: {party.contact}</div>}
      {party?.phone && <div className="text-[11px]">Phone: {party.phone}</div>}
    </div>
  );
}

function SignBlock({ title, bordered }: { title: string; bordered?: boolean }) {
  return (
    <div className={`p-2 h-20 ${bordered ? "border-l border-black" : ""}`}>
      <div className="text-[9px] uppercase tracking-wider text-neutral-600">{title}</div>
      <div className="border-b border-black mt-8" />
    </div>
  );
}